import { ChevronDown, CloudOff, Plus, RefreshCw, SquareTerminal, X } from '@orgus/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { TERM_LIMITS } from '@orgus/protocol';
import type { TermSummary } from '@orgus/protocol';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  RowStatus,
  Segmented,
  Skeleton,
  StateBar,
  cn,
  formatInt,
  plural,
} from '@orgus/ui';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { PageActions } from '../components/shell/TopBar.tsx';
import { useRegisterCommands } from '../lib/commands.ts';
import { useMachines } from '../lib/store.ts';
import { useTerms } from '../features/terminal/api.ts';
import { LAYOUT_OPTIONS, layoutFor, parseShown, pinShown, showOne, termRowStatus, toggleShown, visibleTerms } from '../features/terminal/model.ts';
import type { SplitLayout } from '../features/terminal/model.ts';
import { NewTerminalDialog } from '../features/terminal/NewTerminalDialog.tsx';
import { TerminalView } from '../features/terminal/TerminalView.tsx';
import { WhenRuns } from '../features/server/ModuleAbsent.tsx';
import { PageVerdict } from '../features/glance/PageVerdict.tsx';
import { termStateCounts, termsVerdict } from '../features/terminal/verdict.ts';

const GRID: Record<SplitLayout, string> = {
  1: 'grid-cols-1 grid-rows-1',
  2: 'grid-cols-1 lg:grid-cols-2 grid-rows-1',
  4: 'grid-cols-1 lg:grid-cols-2 lg:grid-rows-2',
};

const PANEL = 'overflow-clip rounded-card border border-border bg-surface';

/** The split view fills the window below the top bar, the filter bar and the page gutter. */
const SPLIT_HEIGHT = { height: 'calc(100dvh - 17rem)' };

/**
 * Which terminals the split view shows, as one quiet menu: each terminal with its status and
 * machine, checked when it is on screen. The panes themselves say the rest.
 */
function TermPicker({ terms, shown, onToggle, machineName }: { terms: TermSummary[]; shown: Set<string>; onToggle: (id: string) => void; machineName: (id: string) => string }) {
  const full = shown.size >= TERM_LIMITS.maxSplit;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" icon={<SquareTerminal />} iconRight={<ChevronDown />}>
          <span className="num">
            {formatInt(shown.size)} of {plural(terms.length, 'terminal')} shown
          </span>
        </Button>
      </DropdownMenuTrigger>
      {/* Wide enough for a session title and its machine on one line; longer titles wrap to a second.
          On a phone it keeps to the content column, clear of the navigation rail. */}
      <DropdownMenuContent align="start" className="w-75 max-w-(--radix-dropdown-menu-content-available-width) sm:w-100">
        <DropdownMenuLabel>Show in grid, up to {TERM_LIMITS.maxSplit}</DropdownMenuLabel>
        {terms.map((t) => (
          <DropdownMenuCheckboxItem
            key={t.id}
            checked={shown.has(t.id)}
            // A title may take two lines (three on a phone): the row grows with it.
            className="h-auto py-2"
            // A fifth replaces the oldest shown, as the toggle always did; say so instead of refusing.
            onSelect={(e) => {
              e.preventDefault();
              onToggle(t.id);
            }}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <RowStatus {...termRowStatus(t)} hideLabel focusable={false} />
              <span className="line-clamp-3 min-w-0 flex-1 whitespace-normal sm:line-clamp-2">{t.title}</span>
              <span className="shrink-0 text-caption text-fg-subtle">{machineName(t.machineId)}</span>
            </span>
          </DropdownMenuCheckboxItem>
        ))}
        {full && <DropdownMenuLabel className="font-normal text-fg-subtle">Another one replaces the oldest shown</DropdownMenuLabel>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SplitSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" style={SPLIT_HEIGHT} role="status" aria-label="Loading terminals">
      {[0, 1].map((i) => (
        <div key={i} className="flex flex-col gap-3 rounded-card border border-border bg-surface p-4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-3 w-3/5" />
        </div>
      ))}
    </div>
  );
}

/** A quiet banner inside the panel for a partial state (stale list, offline machines). */
function PartialBanner({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div
      role="status"
      className="flex min-h-row items-center gap-3 border-b border-border-subtle px-row-x py-2 text-caption text-fg-muted [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-fg-subtle"
    >
      {icon}
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}

/** Terminals: up to four live terminals side by side, each with its own controls and status bar. */
function TerminalsPage() {
  const { terms, phase, error, refresh } = useTerms();
  const machines = useMachines();
  const [params, setParams] = useSearchParams();
  const [newOpen, setNewOpen] = useState(false);
  const [picked, setPicked] = useState<SplitLayout | null>(null);

  const requested = useMemo(() => parseShown(params.get('show')), [params]);
  const shown = useMemo(() => visibleTerms(terms, requested), [terms, requested]);
  const layout = layoutFor(shown.length, picked);
  const displayed = shown.slice(0, layout);
  const machineName = useMemo(() => new Map(machines.map((m) => [m.id, m.name])), [machines]);
  const offline = useMemo(() => new Set(shown.filter((t) => t.state === 'disconnected').map((t) => machineName.get(t.machineId) ?? t.machineId)), [shown, machineName]);

  const setShown = useCallback(
    (ids: string[]) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          // An empty list is a choice too (show none), so it stays in the address.
          n.set('show', ids.join(','));
          return n;
        },
        { replace: true },
      ),
    [setParams],
  );
  const toggle = (id: string) =>
    setShown(
      toggleShown(
        shown.map((t) => t.id),
        id,
      ),
    );
  // Pin what the page picked, so a terminal that ends stays on screen with its last output and a
  // toggle on it hides it instead of bringing it straight back.
  const pin = useMemo(() => pinShown(requested, shown), [requested, shown]);
  useEffect(() => {
    if (pin) setShown(pin);
  }, [pin, setShown]);
  const openNew = useCallback(() => setNewOpen(true), []);

  useRegisterCommands(
    [
      {
        id: 'terminal.new',
        title: 'New terminal',
        group: 'Terminals',
        icon: Plus,
        keywords: ['shell', 'take over', 'pty', 'claude'],
        perform: openNew,
      },
    ],
    [openNew],
  );

  const live = terms.filter((t) => t.state !== 'exited').length;

  return (
    <PageBody wide className="flex flex-col gap-3">
      <PageActions>
        <Button variant="secondary" size="sm" icon={<Plus className="text-accent-text" />} onClick={openNew}>
          New terminal
        </Button>
      </PageActions>
      <DataGate skeleton={<SplitSkeleton />}>
        {phase === 'loading' ? (
          <SplitSkeleton />
        ) : phase === 'error' ? (
          <div className={PANEL}>
            <ErrorState error={error} onRetry={refresh} />
          </div>
        ) : !terms.length ? (
          <div className={PANEL}>
            <EmptyState
              size="page"
              icon={<SquareTerminal />}
              title="No terminals open"
              description="Start Claude Code or a shell on any machine, up to four side by side"
              action={
                <Button variant="primary" icon={<Plus />} onClick={openNew}>
                  New terminal
                </Button>
              }
            />
          </div>
        ) : (
          <>
            <PageVerdict v={termsVerdict(terms)} figure={<StateBar counts={termStateCounts(terms)} label={`${formatInt(live)} live of ${plural(terms.length, 'terminal')}`} className="w-40" />} />
            <div className={PANEL}>
              <div className="flex min-h-filter-bar flex-wrap items-center gap-2 px-2 py-1">
                <TermPicker terms={terms} shown={new Set(displayed.map((t) => t.id))} onToggle={toggle} machineName={(id) => machineName.get(id) ?? 'Unknown machine'} />
                <span className="text-caption text-fg-subtle num">{formatInt(live)} live</span>
                <span className="ml-auto">
                  <Segmented label="Layout" value={String(layout)} onChange={(v) => setPicked(Number(v) as SplitLayout)} options={LAYOUT_OPTIONS} />
                </span>
              </div>
              {error && <PartialBanner icon={<RefreshCw />}>The list could not refresh: {error.why} Showing what was there before.</PartialBanner>}
              {offline.size > 0 && (
                <PartialBanner icon={<CloudOff />}>
                  {[...offline].join(', ')} {offline.size === 1 ? 'is' : 'are'} offline. Those terminals resume when the machine reconnects.
                </PartialBanner>
              )}
            </div>
            {displayed.length ? (
              <div className={cn('grid min-h-96 gap-3', GRID[layout])} style={SPLIT_HEIGHT}>
                {displayed.map((t) => (
                  <TerminalView
                    key={t.id}
                    termId={t.id}
                    compact={layout > 1}
                    headingLevel={2}
                    className="min-h-0"
                    // On a phone the toggles above do this, and the title needs the room.
                    actions={<IconButton label="Remove from the split view" icon={<X />} size="sm" className="hidden sm:inline-flex" onClick={() => toggle(t.id)} />}
                  />
                ))}
              </div>
            ) : (
              <div className={PANEL}>
                <EmptyState icon={<SquareTerminal />} title="No terminal picked" description="Pick up to four in the menu above" />
              </div>
            )}
          </>
        )}
      </DataGate>
      <NewTerminalDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onOpened={(id) =>
          setShown(
            showOne(
              shown.map((t) => t.id),
              id,
            ),
          )
        }
      />
    </PageBody>
  );
}

export default function Terminals() {
  return (
    <WhenRuns module="term" feature="Terminals" plural instead="Each session shows its output live in Sessions." size="page">
      <TerminalsPage />
    </WhenRuns>
  );
}
